import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import TermsAndConditions from "@/pages/TermsAndConditions";
import { Link } from "react-router-dom";
import {
  Airplane, Trophy, ArrowLeft, ArrowRight, Medal, Clock, Calculator,
  Sparkle, CalendarBlank, UserPlus, TelegramLogo,
} from "@phosphor-icons/react";
import { useTrackPageView } from "@/lib/useTrackPageView";

const formatDate = (d) =>
  d.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });

function computeETA(lotsPerDay, targetLots, campaignStart, campaignEnd) {
  const rate = Number(lotsPerDay);
  const goal = Number(targetLots);
  if (!rate || rate <= 0 || !goal) return { eta: null, before: null, days: null };
  const now = new Date();
  const start = new Date(campaignStart);
  const end = new Date(campaignEnd);
  const anchor = now > start ? now : start;
  const daysNeeded = Math.ceil(goal / rate);
  const eta = new Date(anchor.getTime() + daysNeeded * 86400 * 1000);
  const before = eta <= end;
  return { eta, before, days: daysNeeded };
}

const MilestoneCalculator = ({ settings }) => {
  const [lots, setLots] = useState(0.5);
  const milestones = settings?.milestones || [];

  const results = useMemo(() => {
    if (!settings) return [];
    return milestones.map((m) => ({
      ...m,
      ...computeETA(lots, m.lot, settings.campaign_start, settings.campaign_end),
    }));
  }, [lots, milestones, settings]);

  const bali = results[results.length - 1];
  const canShare = lots > 0 && bali?.before;

  const presets = [0.25, 0.5, 1, 2, 5];

  return (
    <div
      data-testid="milestone-calculator"
      className="bg-gradient-to-br from-[#111827] via-[#0F1a2e] to-[#111827] border border-orange-500/20 rounded-xl p-5 md:p-6 mb-6 relative overflow-hidden"
    >
      <div className="absolute -top-16 -right-16 w-48 h-48 bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-start gap-3 mb-4">
          <Calculator size={22} weight="duotone" className="text-orange-400 mt-0.5" />
          <div>
            <h2 className="font-display text-lg md:text-xl font-bold text-white">Bali Trip Calculator</h2>
            <p className="text-xs md:text-sm text-gray-400">See when YOU can reach the milestones.</p>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-[11px] uppercase tracking-widest text-gray-400 mb-2">Your daily trade volume (lots)</label>
          <div className="flex flex-wrap items-center gap-2">
            <input
              data-testid="calc-lots-input"
              type="number"
              min="0"
              step="0.25"
              value={lots}
              onChange={(e) => setLots(Number(e.target.value))}
              className="w-28 bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-white text-sm font-mono focus:border-orange-500 outline-none"
            />
            <span className="text-xs text-gray-500">lots / day</span>
            <div className="flex gap-1 ml-2">
              {presets.map((p) => (
                <button
                  key={p}
                  data-testid={`calc-preset-${p}`}
                  onClick={() => setLots(p)}
                  className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                    lots === p
                      ? "border-orange-500 text-orange-300 bg-orange-500/10"
                      : "border-white/10 text-gray-400 hover:text-white hover:border-white/20"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          {results.map((r, i) => (
            <div
              key={i}
              data-testid={`calc-milestone-${r.lot}`}
              className={`p-4 rounded-lg border ${
                r.before ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10 bg-[#0B0F19]"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-widest text-gray-500">
                  {r.label || `${r.lot} lots`}
                </p>
                {i === results.length - 1 ? (
                  <Airplane size={14} className="text-orange-400" />
                ) : (
                  <Medal size={14} className="text-blue-400" />
                )}
              </div>
              <p className="text-2xl font-bold text-white font-mono">{r.lot}</p>
              <p className="text-[11px] text-gray-500">
                {r.reward > 0 ? `$${r.reward} reward` : "Bali Trip eligibility"}
              </p>
              <div className="mt-3 pt-3 border-t border-white/5">
                {r.eta ? (
                  <>
                    <div className="flex items-center gap-1.5 text-xs">
                      <CalendarBlank size={12} className={r.before ? "text-emerald-400" : "text-red-400"} />
                      <span className={r.before ? "text-emerald-300 font-semibold" : "text-red-300"}>
                        {formatDate(r.eta)}
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-500 mt-1">
                      {r.days} day{r.days > 1 ? "s" : ""}
                      {!r.before && " — after campaign ends"}
                    </p>
                  </>
                ) : (
                  <p className="text-[11px] text-gray-500">Enter your daily volume</p>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-4 rounded-lg bg-gradient-to-br from-orange-500/15 via-orange-500/5 to-transparent border border-orange-500/30">
          <div className="flex-1 flex items-start gap-2">
            <Sparkle size={16} className="text-orange-400 mt-0.5" weight="fill" />
            <div className="text-sm text-orange-100">
              <p className="font-semibold text-white">Ready to join the challenge?</p>
              <p className="text-xs text-orange-100/80 mt-0.5">
                {canShare ? (
                  <>You&apos;re on track for Bali by <strong>{formatDate(bali.eta)}</strong>. Contact our admin to register &amp; get onboarded.</>
                ) : (
                  <>Open an XM account with our referral and start racking up valid SILVER lots. Contact admin to get onboarded.</>
                )}
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Link
              to="/register"
              data-testid="calc-join-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-white/10 hover:bg-white/15 border border-white/15 text-white text-sm font-medium transition-colors"
            >
              <UserPlus size={14} weight="duotone" />
              Register
            </Link>
            <a
              href={settings?.admin_telegram_url || "https://t.me/mbealert"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="calc-telegram-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-orange-500 hover:bg-orange-400 text-white text-sm font-medium transition-colors"
            >
              <TelegramLogo size={14} weight="fill" />
              {settings?.admin_telegram_label || "Contact Admin"}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function PublicLeaderboard() {
  useTrackPageView("leaderboard");
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  useEffect(() => {
    (async () => {
      try {
        const [lb, ss] = await Promise.all([
          api.get("/leaderboard/public"),
          api.get("/campaign-settings/public"),
        ]);
        setRows(lb.data);
        setSettings(ss.data);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const pageStart = rows.length === 0 ? 0 : page * PAGE_SIZE + 1;
  const pageEnd = Math.min((page + 1) * PAGE_SIZE, rows.length);

  return (
    <div className="min-h-screen bg-[#0B0F19] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(37,99,235,0.15),transparent_50%),radial-gradient(circle_at_80%_80%,rgba(249,115,22,0.15),transparent_50%)]" />
      <div className="relative z-10 max-w-3xl mx-auto px-4 py-12">
        <Link to="/login" className="inline-flex items-center gap-2 text-xs text-gray-400 hover:text-white mb-6">
          <ArrowLeft size={14} /> Back to login
        </Link>
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_mbe-bali-tracker/artifacts/8ljitr27_PROFILE%20ALL%20CHANNEL-05.jpg"
            alt="MBE International"
            className="w-24 h-24 rounded-full object-cover mx-auto mb-4 ring-2 ring-orange-500/40 shadow-[0_0_40px_rgba(249,115,22,0.25)]"
          />
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/30 mb-3">
            <Airplane size={14} className="text-orange-400" />
            <span className="text-xs uppercase tracking-widest font-mono text-orange-300">Live Leaderboard</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-2">Road To Bali</h1>
          <p className="text-sm text-gray-400">Names masked for privacy. Updated as broker reports are imported.</p>
          {settings?.days_remaining !== undefined && settings.campaign_status === "live" && (
            <p className="mt-2 text-xs font-mono text-orange-300">{settings.days_remaining} days remaining</p>
          )}
          {settings?.campaign_status === "upcoming" && (
            <p className="mt-2 text-xs font-mono text-blue-300">Campaign starts {formatDate(new Date(settings.campaign_start))}</p>
          )}
        </div>

        {settings && <MilestoneCalculator settings={settings} />}

        <div className="bg-[#111827] border border-white/10 rounded-xl overflow-hidden">
          <div className="grid grid-cols-12 px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-widest text-gray-500 font-medium">
            <div className="col-span-1">Rank</div>
            <div className="col-span-4">Trader</div>
            <div className="col-span-3">Account</div>
            <div className="col-span-2 text-right">Valid Lot</div>
            <div className="col-span-2 text-right">Milestone</div>
          </div>
          {loading ? (
            <div className="p-8 text-center text-gray-500 text-sm">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">No qualifying traders yet.</div>
          ) : (
            pageRows.map((r) => (
              <div
                key={r.rank}
                data-testid={`public-row-${r.rank}`}
                className={`grid grid-cols-12 px-5 py-3 items-center text-sm border-b border-white/5 ${r.rank <= 3 ? "bg-orange-500/5" : ""}`}
              >
                <div className="col-span-1">
                  {r.rank <= 3 ? (
                    <Trophy weight="fill" size={20} className={r.rank === 1 ? "text-yellow-400" : r.rank === 2 ? "text-gray-300" : "text-orange-400"} />
                  ) : (
                    <span className="font-mono text-gray-500">#{r.rank}</span>
                  )}
                </div>
                <div className="col-span-4 text-white font-medium flex items-center gap-1.5">
                  <span className="truncate">{r.name}</span>
                  {r.funded && (
                    <span
                      data-testid={`funded-marker-${r.rank}`}
                      title="Funded broker account"
                      className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[9px] font-bold flex-shrink-0"
                    >
                      $
                    </span>
                  )}
                </div>
                <div className="col-span-3 font-mono text-gray-400 text-xs">{r.account}</div>
                <div className="col-span-2 text-right font-mono text-white">{r.total_valid_lot.toFixed(2)}</div>
                <div className="col-span-2 text-right">
                  {r.bali_qualified ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-300 border border-orange-500/20">
                      <Airplane size={10} /> Bali
                    </span>
                  ) : r.pending_bali_audit ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30">
                      <Clock size={10} weight="fill" /> Pending Audit
                    </span>
                  ) : r.current_milestone > 0 ? (
                    <span className="inline-flex items-center gap-1 text-xs font-mono text-blue-300">
                      <Medal size={12} /> {r.current_milestone}
                    </span>
                  ) : (
                    <span className="text-gray-600">—</span>
                  )}
                </div>
              </div>
            ))
          )}
          {rows.length > PAGE_SIZE && (
            <div
              data-testid="leaderboard-pagination"
              className="flex items-center justify-between px-5 py-3 border-t border-white/10 bg-[#0F1522]"
            >
              <button
                data-testid="pagination-prev"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white/5 border border-white/10 text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowLeft size={12} weight="bold" /> Prev
              </button>
              <p className="text-[11px] text-gray-500 font-mono">
                Showing <span className="text-white">#{pageStart}–#{pageEnd}</span> of {rows.length}
                <span className="hidden sm:inline"> · page {page + 1} / {totalPages}</span>
              </p>
              <button
                data-testid="pagination-next"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-orange-500/15 border border-orange-500/30 text-orange-200 hover:bg-orange-500/25 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Next <ArrowRight size={12} weight="bold" />
              </button>
            </div>
          )}
        </div>

        {/* Terms & Conditions collapsible section */}
        <details data-testid="terms-collapse" className="mt-8 bg-[#111827] border border-white/10 rounded-xl overflow-hidden group">
          <summary className="cursor-pointer px-5 py-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors">
            <span className="flex items-center gap-2 text-sm font-medium text-white">
              <span className="text-orange-400 text-lg">📋</span>
              Terms &amp; Conditions
            </span>
            <span className="text-xs text-gray-500 group-open:hidden">Tap to expand</span>
            <span className="text-xs text-gray-500 hidden group-open:inline">Collapse</span>
          </summary>
          <div className="p-5 pt-2 border-t border-white/5 bg-[#0B0F19]">
            <TermsAndConditions embedded />
            <div className="mt-4 pt-4 border-t border-white/5 text-center">
              <Link
                to="/terms"
                data-testid="terms-full-page-link"
                className="text-xs text-blue-400 hover:text-blue-300 hover:underline"
              >
                View full T&amp;C in a dedicated page →
              </Link>
            </div>
          </div>
        </details>
      </div>
    </div>
  );
}
